import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
import { heroFacts, profile } from '../data/profile';
import { navItems } from '../data/navigation';
import { PortraitFrame } from '../components/PortraitFrame';
import { Reveal } from '../components/Reveal';
export function Home() {
  const reduce = useReducedMotion();
  const enter = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease: [0.23, 1, 0.32, 1]
    }
  });
  return <>
      <section aria-label="Introduction" className="bg-ink text-paper" data-id="element-293" data-mp="29i7xjx1s1we0">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-14 md:py-20 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-294" data-mp="2dii2d6du2gra">
          <motion.div {...enter(0)} className="lg:col-span-5" data-id="element-295" data-mp="3kxd3d9x7gb1u">
            <PortraitFrame data-id="element-296" data-mp="3ovud8180fzmy" />
          </motion.div>

          <div className="lg:col-span-7" data-id="element-297" data-mp="30wrpwio1y1gl">
            <motion.p {...enter(0.05)} className="flex items-center gap-2 text-sm text-bronze-light" data-id="element-298" data-mp="30vqfmgev1izx">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-299" data-mp="2vyq4hr35lq1y" />
              {profile.location}
            </motion.p>
            <motion.h2 {...enter(0.1)} className="mt-6 font-serif text-4xl font-normal leading-[1.1] tracking-tight md:text-5xl xl:text-6xl" data-id="element-300" data-mp="2qwsgyknq0937">
              Connecting technology strategy with business growth across Latin America.
            </motion.h2>
            <motion.p {...enter(0.15)} className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-light" data-id="element-301" data-mp="2d6ozs83p75km">
              {profile.intro}
            </motion.p>
            <motion.div {...enter(0.2)} className="mt-9 flex flex-wrap gap-4" data-id="element-302" data-mp="28618dlbk4mpv">
              <Link to="/about" className="inline-flex items-center gap-2 rounded-sm bg-paper px-6 py-3.5 text-sm font-medium text-ink transition-colors duration-150 hover:bg-paper-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-light focus-visible:ring-offset-2 focus-visible:ring-offset-ink" data-id="element-303" data-mp="23alx914xp4x5">
                Read the profile
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-304" data-mp="1onvl14po9rws" />
              </Link>
              <Link to="/experience" className="inline-flex items-center rounded-sm border border-line-dark px-6 py-3.5 text-sm font-medium text-paper transition-colors duration-150 hover:border-slate-light focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-light" data-id="element-305" data-mp="1ao42qrs4hpcz">
                Career experience
              </Link>
            </motion.div>
            <motion.dl {...enter(0.25)} className="mt-12 grid gap-x-8 gap-y-6 border-t border-line-dark pt-8 sm:grid-cols-2" data-id="element-306" data-mp="1tl59oi58lzjk">
              {heroFacts.map(fact => <div key={fact.label} data-id="element-307" data-mp="2m91fqdgp53t0">
                  <dt className="font-serif text-xl text-paper" data-id="element-308" data-mp="2m7zrt8el0mtv">{fact.label}</dt>
                  <dd className="mt-1 text-sm text-slate-light" data-id="element-309" data-mp="2r2dpxikmp828">{fact.detail}</dd>
                </div>)}
            </motion.dl>
          </div>
        </div>
      </section>

      <section aria-labelledby="sections-title" className="bg-paper py-20 md:py-28" data-id="element-310" data-mp="alxx31o8i917">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-311" data-mp="oedwlshxfoxd">
          <Reveal data-id="element-312" data-mp="ofyrjj85l9vr">
            <h2 id="sections-title" className="font-serif text-3xl text-ink md:text-4xl" data-id="element-313" data-mp="kc0o4wbspcww">
              Explore the profile
            </h2>
            <p className="mt-3 text-base text-slate-text" data-id="element-314" data-mp="3ssvskg88r5d3">Nine sections, each covering one part of his career.</p>
          </Reveal>
          <ul className="mt-12 grid gap-px border-y border-ink bg-line sm:grid-cols-2 lg:grid-cols-3" data-id="element-315" data-mp="3nvlwihqcjvga">
            {navItems.slice(1).map(item => <li key={item.path} className="bg-paper" data-id="element-316" data-mp="ni937ki7fvx">
                <Link to={item.path} className="group flex h-full items-start justify-between gap-6 px-1 py-7 transition-colors duration-150 hover:bg-paper-deep sm:px-6" data-id="element-317" data-mp="1h2t4zw6jz8zq">
                  <div data-id="element-318" data-mp="27eohk2p0fgpq">
                    <span className="text-sm tabular-nums text-bronze" data-id="element-319" data-mp="27f6ivdqj4l4i">{item.number}</span>
                    <p className="mt-1 font-serif text-2xl text-ink" data-id="element-320" data-mp="2cdrbos3gc2ix">{item.label}</p>
                    <p className="mt-1.5 text-sm text-slate-text" data-id="element-321" data-mp="2hbt3c1ey1a60">{item.description}</p>
                  </div>
                  <ArrowRightIcon className="mt-7 h-4 w-4 shrink-0 text-slate-text transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden="true" data-id="element-322" data-mp="2mc76b51ottx6" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}